import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { useAuth } from "../context/AuthContext";
import { Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function AuthDialog({ open, onClose, defaultTab = "login", onSuccess }) {
  const [tab, setTab] = useState(defaultTab);
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md p-0 overflow-hidden" data-testid="auth-dialog">
        <DialogHeader className="px-6 pt-6">
          <DialogTitle className="font-heading font-black text-2xl">
            {tab === "login" ? "Bienvenido de vuelta" : "Crea tu cuenta"}
          </DialogTitle>
        </DialogHeader>
        <div className="flex gap-1 px-6 mt-4 border-b border-slate-200">
          <TabBtn active={tab === "login"} onClick={() => setTab("login")} testId="auth-tab-login">Ingresar</TabBtn>
          <TabBtn active={tab === "register"} onClick={() => setTab("register")} testId="auth-tab-register">Registrarse</TabBtn>
        </div>
        <div className="px-6 pb-6 pt-5">
          {tab === "login" ? (
            <LoginForm onSuccess={(u) => { onSuccess?.(u); onClose(); }} />
          ) : (
            <RegisterForm onSuccess={(u) => { onSuccess?.(u); onClose(); }} />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

const TabBtn = ({ active, onClick, children, testId }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className={`px-4 py-2.5 font-bold text-sm border-b-2 transition ${active ? "border-[#0EA5E9] text-black" : "border-transparent text-slate-500 hover:text-slate-700"}`}
  >
    {children}
  </button>
);

const LoginForm = ({ onSuccess }) => {
  const { login } = useAuth();
  const [correo, setCorreo] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!correo || !password) { toast.error("Completa todos los campos"); return; }
    setLoading(true);
    try {
      const u = await login(correo, password);
      toast.success(`¡Hola ${u.nombre}!`);
      onSuccess?.(u);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al iniciar sesión");
    } finally { setLoading(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-3">
      <Field label="Correo" type="email" value={correo} onChange={setCorreo} testId="login-correo" />
      <Field label="Contraseña" type="password" value={password} onChange={setPassword} testId="login-password" />
      <button type="submit" disabled={loading} className="w-full btn-primary inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="login-submit">
        {loading ? <><Loader2 size={16} className="animate-spin" /> Ingresando...</> : <><Sparkles size={16} /> Ingresar</>}
      </button>
    </form>
  );
};

const RegisterForm = ({ onSuccess }) => {
  const { register } = useAuth();
  const [form, setForm] = useState({ nombre: "", apellido: "", rut: "", telefono: "", correo: "", direccion: "", password: "" });
  const [loading, setLoading] = useState(false);

  const setF = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    for (const k of Object.keys(form)) {
      if (!form[k]?.trim()) { toast.error(`Completa el campo ${k}`); return; }
    }
    if (form.password.length < 6) { toast.error("La contraseña debe tener mínimo 6 caracteres"); return; }
    setLoading(true);
    try {
      const u = await register(form);
      toast.success(`¡Bienvenido ${u.nombre}! 🎉`);
      onSuccess?.(u);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al registrar");
    } finally { setLoading(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-2.5">
      <div className="grid grid-cols-2 gap-2.5">
        <Field label="Nombre" value={form.nombre} onChange={(v) => setF("nombre", v)} testId="reg-nombre" />
        <Field label="Apellido" value={form.apellido} onChange={(v) => setF("apellido", v)} testId="reg-apellido" />
      </div>
      <Field label="RUT" value={form.rut} onChange={(v) => setF("rut", v)} placeholder="12.345.678-9" testId="reg-rut" />
      <Field label="Teléfono" value={form.telefono} onChange={(v) => setF("telefono", v)} placeholder="+56 9 1234 5678" testId="reg-telefono" />
      <Field label="Correo" type="email" value={form.correo} onChange={(v) => setF("correo", v)} testId="reg-correo" />
      <Field label="Dirección" value={form.direccion} onChange={(v) => setF("direccion", v)} testId="reg-direccion" />
      <Field label="Contraseña" type="password" value={form.password} onChange={(v) => setF("password", v)} testId="reg-password" />
      <button type="submit" disabled={loading} className="w-full btn-primary inline-flex items-center justify-center gap-2 disabled:opacity-60 mt-2" data-testid="register-submit">
        {loading ? <><Loader2 size={16} className="animate-spin" /> Registrando...</> : <>Crear cuenta</>}
      </button>
    </form>
  );
};

const Field = ({ label, value, onChange, type = "text", placeholder = "", testId }) => (
  <div>
    <label className="block text-xs uppercase tracking-widest font-bold text-slate-600 mb-1">{label}</label>
    <input
      type={type} value={value} placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testId}
      className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/30 outline-none text-sm"
    />
  </div>
);
